/**
 * StaleContactBanner — top-of-modal urgency alert.
 *
 * Surfaces the single most decision-driving signal a CSR needs to see
 * the moment a Resolve / Push modal opens: how long the customer has
 * been waiting. Tap-to-call.
 *
 * Source of truth (in priority order):
 *   1. Last logged contact in job.activityHistory
 *   2. Issue age (issue.dateCreated) — the issue itself is the wait
 *
 * Returns null only when both are unknown OR wait < 3 days.
 */
import { Clock, PhoneCall } from "lucide-react";
import type { Job, Issue } from "@/data/types";
import { cn } from "@/lib/utils";

interface Props {
  job: Job;
  issue?: Issue | null;
  onCallClick?: () => void;
  className?: string;
}

function daysSinceIso(iso?: string): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
}

function lastContactDaysFor(job: Job): number | null {
  const history = job.activityHistory ?? [];
  const touch = [...history]
    .reverse()
    .find((h) => /call|sms|text|email|message|contact|note/i.test(h.action));
  return daysSinceIso(touch?.date);
}

export function StaleContactBanner({ job, issue, onCallClick, className }: Props) {
  const contactDays = lastContactDaysFor(job);
  const issueDays = daysSinceIso(issue?.dateCreated);

  // Use whichever signal exists. If both, the issue age is what the
  // customer actually feels — we've had this open without resolution.
  const days = issueDays ?? contactDays;
  const source: "issue" | "contact" | null =
    issueDays !== null ? "issue" : contactDays !== null ? "contact" : null;

  if (days === null || source === null || days < 3) return null;

  const critical = days >= 5;
  const bg = critical
    ? "bg-destructive text-destructive-foreground"
    : "bg-warning text-warning-foreground";
  const cta = critical ? "Call now" : "Reach out today";
  const label = source === "issue" ? `Customer waiting ${days} days` : `No contact in ${days} days`;

  return (
    <button
      type="button"
      onClick={onCallClick}
      className={cn(
        "w-full rounded-lg px-4 py-2.5 flex items-center justify-between gap-3 text-sm font-medium shadow-sm transition-all hover:opacity-95 hover:shadow-md active:scale-[0.99]",
        bg,
        className,
      )}
    >
      <span className="flex items-center gap-2">
        <Clock className="h-4 w-4 shrink-0" />
        <span className="truncate">{label}</span>
      </span>
      <span className="flex items-center gap-1.5 text-xs font-semibold opacity-95 shrink-0">
        <PhoneCall className="h-3.5 w-3.5" />
        {cta}
      </span>
    </button>
  );
}

/** Exposed so the modal can suppress its own redundant risk-signal banner. */
export function staleContactDays(job: Job, issue?: Issue | null): number | null {
  const issueDays = daysSinceIso(issue?.dateCreated);
  if (issueDays !== null) return issueDays;
  return lastContactDaysFor(job);
}
